'use client';
import React, { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { Tag, Film, Tv, Download, Star, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import AdBanner from '@/app/components/AdBanner';

interface ContentItem {
  id: string;
  title: string;
  slug: string;
  year: number;
  imdb_rating: number;
  genre: string;
  poster_url: string;
  type: 'movie' | 'series';
}

interface GenreGroup {
  genre: string;
  items: ContentItem[];
}

export default function GenresContent() {
  const [movies, setMovies] = useState<ContentItem[]>([]);
  const [series, setSeries] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState<'all' | 'movie' | 'series'>('all');
  const [selectedGenre, setSelectedGenre] = useState<string | null>(null);
  const [expandedGenres, setExpandedGenres] = useState<Set<string>>(new Set());
  const supabase = createClient();

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      const [moviesRes, seriesRes] = await Promise.all([
        supabase
          .from('movies')
          .select('id, title, slug, year, imdb_rating, genre, poster_url')
          .order('created_at', { ascending: false }),
        supabase
          .from('series')
          .select('id, title, slug, year, imdb_rating, genre, poster_url')
          .order('created_at', { ascending: false }),
      ]);
      setMovies((moviesRes.data ?? []).map((m) => ({ ...m, type: 'movie' as const })));
      setSeries((seriesRes.data ?? []).map((s) => ({ ...s, type: 'series' as const })));
      setLoading(false);
    };
    fetchAll();
  }, []);

  const allItems = useMemo(() => {
    const base = activeType === 'all'
      ? [...movies, ...series]
      : activeType === 'movie' ? movies : series;
    return base.filter((i) => i.genre);
  }, [movies, series, activeType]);

  const genreGroups = useMemo((): GenreGroup[] => {
    const map = new Map<string, ContentItem[]>();
    allItems.forEach((item) => {
      const g = item.genre?.trim();
      if (!g) return;
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(item);
    });
    return Array.from(map.entries())
      .map(([genre, items]) => ({ genre, items }))
      .sort((a, b) => a.genre.localeCompare(b.genre));
  }, [allItems]);

  const allGenres = useMemo(() => genreGroups.map((g) => g.genre), [genreGroups]);

  const displayedGroups = useMemo(() => {
    if (selectedGenre) return genreGroups.filter((g) => g.genre === selectedGenre);
    return genreGroups;
  }, [genreGroups, selectedGenre]);

  const toggleExpand = (genre: string) => {
    setExpandedGenres((prev) => {
      const next = new Set(prev);
      if (next.has(genre)) next.delete(genre);
      else next.add(genre);
      return next;
    });
  };

  const getHref = (item: ContentItem) =>
    item.type === 'movie'
      ? `/movie-detail-page?slug=${item.slug}`
      : `/series/${item.slug}`;

  const PREVIEW_COUNT = 6;

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-9 h-9 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center">
            <Tag size={18} className="text-primary" />
          </div>
          <h1 className="text-3xl font-extrabold text-gradient-cyan">Genres</h1>
        </div>
        <p className="text-muted-foreground font-body text-sm">
          {loading ? 'Loading...' : `${genreGroups.length} genres across ${allItems.length} titles`}
        </p>
      </div>

      {/* Type filter */}
      <div className="flex gap-2 mb-6">
        {[
          { label: 'All', value: 'all' },
          { label: 'Movies', value: 'movie' },
          { label: 'Series', value: 'series' },
        ].map((tab) => (
          <button
            key={`type-${tab.value}`}
            onClick={() => { setActiveType(tab.value as typeof activeType); setSelectedGenre(null); }}
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 border ${
              activeType === tab.value
                ? 'neon-border-cyan text-primary bg-primary/10' :'border-border text-muted-foreground hover:text-foreground hover:border-border/60'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Genre chips */}
      {!loading && allGenres.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-8">
          <button
            onClick={() => setSelectedGenre(null)}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 border ${
              !selectedGenre
                ? 'neon-border-cyan text-primary bg-primary/10' :'border-border text-muted-foreground hover:text-foreground hover:border-border/60'
            }`}
          >
            All Genres
          </button>
          {allGenres.map((g) => (
            <button
              key={`chip-${g}`}
              onClick={() => setSelectedGenre(selectedGenre === g ? null : g)}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 border ${
                selectedGenre === g
                  ? 'neon-border-cyan text-primary bg-primary/10' :'border-border text-muted-foreground hover:text-foreground hover:border-border/60'
              }`}
            >
              {g}
            </button>
          ))}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="glass-card border border-border rounded-2xl p-16 flex flex-col items-center justify-center gap-4">
          <Loader2 size={32} className="animate-spin text-primary" />
          <p className="text-sm text-muted-foreground font-body">Loading genres...</p>
        </div>
      ) : displayedGroups.length === 0 ? (
        <div className="glass-card rounded-2xl p-16 text-center border border-border">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Tag size={28} className="text-primary" />
          </div>
          <h3 className="text-lg font-bold text-foreground mb-2">No content yet</h3>
          <p className="text-sm text-muted-foreground font-body">
            Content added from the admin dashboard will appear here grouped by genre.
          </p>
        </div>
      ) : (
        <div className="space-y-10">
          {displayedGroups.map(({ genre, items }, groupIdx) => {
            const isExpanded = expandedGenres.has(genre);
            const displayed = isExpanded ? items : items.slice(0, PREVIEW_COUNT);
            const hasMore = items.length > PREVIEW_COUNT;

            return (
              <React.Fragment key={`genre-section-${genre}`}>
                <section>
                  {/* Genre heading */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <h2 className="text-xl font-bold text-foreground">{genre}</h2>
                      <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/20">
                        {items.length}
                      </span>
                    </div>
                    {hasMore && (
                      <button
                        onClick={() => toggleExpand(genre)}
                        className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-primary transition-colors"
                      >
                        {isExpanded ? (
                          <><ChevronUp size={14} /> Show less</>
                        ) : (
                          <><ChevronDown size={14} /> Show all {items.length}</>
                        )}
                      </button>
                    )}
                  </div>

                  {/* Items grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                    {displayed.map((item) => (
                      <Link key={`genre-item-${item.id}`} href={getHref(item)} className="block group">
                        <div className="glass-card glass-card-hover rounded-xl overflow-hidden cursor-pointer">
                          <div className="relative aspect-[2/3] overflow-hidden">
                            <AppImage
                              src={item.poster_url || '/assets/images/no_image.png'}
                              alt={`${item.title} — ${item.genre} ${item.year}`}
                              fill
                              sizes="(max-width: 640px) 50vw, (max-width: 1024px) 25vw, 16vw"
                              className="object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent opacity-70" />

                            {/* Type badge */}
                            <div className="absolute top-2 left-2">
                              <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold backdrop-blur-sm ${
                                item.type === 'movie' ?'bg-blue-500/20 text-blue-400 border border-blue-500/30' :'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                              }`}>
                                {item.type === 'movie' ? <Film size={9} /> : <Tv size={9} />}
                                {item.type === 'movie' ? 'Movie' : 'Series'}
                              </span>
                            </div>

                            {/* Rating */}
                            {item.imdb_rating && (
                              <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/60 backdrop-blur-sm rounded-full px-2 py-1">
                                <Star size={10} className="rating-star fill-yellow-400" />
                                <span className="text-xs font-bold font-mono-data">{item.imdb_rating?.toFixed(1)}</span>
                              </div>
                            )}

                            {/* Hover overlay */}
                            <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/10 transition-all duration-300 flex items-center justify-center">
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                <div className="flex items-center gap-1.5 px-3 py-1.5 neon-btn-solid-cyan rounded-full text-xs font-bold">
                                  <Download size={12} />
                                  View
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="p-3">
                            <h3 className="text-sm font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                              {item.title}
                            </h3>
                            <span className="text-xs text-muted-foreground font-mono-data">{item.year}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
                {/* Ad: Between genres — show after every 2nd genre section */}
                {(groupIdx + 1) % 2 === 0 && groupIdx < displayedGroups.length - 1 && (
                  <AdBanner placement="between_genres" className="py-2" />
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}
